/**
 * El cliente de la API.
 *
 * ── LA DECISIÓN QUE SOSTIENE TODO ───────────────────────────────────────────────
 *
 * Los tipos de aquí **no tienen** `riskScore` ni `subjectHash`. No es que no los pintemos:
 * es que **no existen en el tipo**, así que ningún componente puede leerlos aunque el
 * servidor un día los mandara por error.
 *
 * `signals` y `reasons` son la excepción medida: existen SOLO como campos opcionales del
 * expediente (abajo), porque el servidor únicamente los emite cuando el actor es ADMIN —
 * el panel también lo usan los admins internos, y su trabajo es auditar el expediente.
 * Para un cliente esos campos no llegan nunca, y el front no decide: dibuja lo que llegó.
 *
 * Es la misma frontera que la API defiende del otro lado, repetida aquí a propósito. Dos
 * cerraduras: si alguien abre la del backend por descuido, el front sigue sin poder mostrar
 * lo que su tipo no contempla.
 *
 * ── UNA ADVERTENCIA QUE COSTÓ UNA PANTALLA EN BLANCO ───────────────────────────
 *
 * Un tipo aquí es una AFIRMACIÓN sobre lo que el servidor devuelve. Si la afirmación es
 * falsa, TypeScript no se entera: compila feliz y la app revienta en el navegador con
 * "Cannot read properties of undefined".
 *
 * Pasó con `/auth/me`: declaré `{ email, role }` porque me pareció lo natural, y el servidor
 * devuelve `{ user: {...}, organization: {...} }`. El typecheck pasó — porque yo le había
 * mentido al tipo — y la pantalla salió en blanco.
 *
 * REGLA: antes de escribir un tipo de respuesta, LLAMAR al endpoint y mirar el JSON. Un tipo
 * inventado es peor que no tener tipos: da confianza falsa.
 */

export class ErrorApi extends Error {
  constructor(
    readonly status: number,
    message: string,
    /** true si el mensaje del servidor dice explícitamente que se devolvió el cobro. */
    readonly reembolsado: boolean,
  ) {
    super(message);
    this.name = 'ErrorApi';
  }
}

const BASE = import.meta.env['VITE_API_URL'] ?? '/api';

const CLAVE_SESION = 'validate.sesion';

export const sesion = {
  get: (): string | null => localStorage.getItem(CLAVE_SESION),
  set: (t: string) => localStorage.setItem(CLAVE_SESION, t),
  limpiar: () => localStorage.removeItem(CLAVE_SESION),
};

async function pedir<T>(ruta: string, init?: RequestInit): Promise<T> {
  const token = sesion.get();

  const r = await fetch(`${BASE}${ruta}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...init?.headers,
    },
  });

  if (r.status === 401) {
    sesion.limpiar();
    location.href = '/entrar';
    throw new ErrorApi(401, 'Tu sesión venció.', false);
  }

  if (!r.ok) {
    const cuerpo: unknown = await r.json().catch(() => null);
    const msg =
      cuerpo && typeof cuerpo === 'object' && 'message' in cuerpo
        ? String((cuerpo as { message: unknown }).message)
        : `Error ${r.status}`;

    // La API promete decir "el cobro fue devuelto" cuando la fuente falla. Lo detectamos
    // aquí para que la UI lo muestre distinto: es la diferencia entre un error que le
    // costó plata al cliente y uno que no.
    throw new ErrorApi(r.status, msg, /cobro fue devuelto|no se te cobr/i.test(msg));
  }

  return (await r.json()) as T;
}

// ── Tipos ────────────────────────────────────────────────────────────────────────

export type Estado = 'aprobado' | 'rechazado' | 'revision';
export type Exposicion = 'VERDICT_ONLY' | 'VERDICT_AND_RULE' | 'RISK_LEVEL' | 'FILE';
export type ModoRevision = 'MANAGED' | 'SELF';
export type Tramo = 'BASE' | 'PRO' | 'ESCALA' | 'CORPORATIVO';
export type Rol = 'ADMIN' | 'CLIENT';

/**
 * Lo que devuelve `/auth/me`. **Comprobado contra la API real**, no supuesto:
 *
 *   { user: { id, email, name, role, identityVerified },
 *     organization: { id, name, countryCode, balance } }
 */
export interface Yo {
  user: {
    id: string;
    email: string;
    name: string;
    role: Rol;
    identityVerified: boolean;
  };
  organization: {
    id: string;
    name: string;
    countryCode: string;
    balance: string;
  };
}

export interface Persona {
  nombres: string;
  apellidoPaterno: string;
  apellidoMaterno: string;
  /** Ya viene enmascarado del servidor: `····3333`. */
  documento: string;
}

// ── El expediente (SOLO llega con rol ADMIN) ─────────────────────────────────────
//
// Estos tipos NO están inventados: son copia de lo que `evidencia.service.ts` guarda en
// `signals` y de `CotejoDeDatos` en `packages/core/src/verificacion/cotejo.ts`. Todo es
// opcional/nulable porque el expediente crece con el producto y una verificación vieja
// no tiene los campos nuevos — un tipo rígido aquí rompería el historial entero.

export type CampoDelCotejo = 'coincide' | 'no_coincide' | 'no_comprobable';

export interface CotejoDeIdentidad {
  nombres: CampoDelCotejo;
  apellidos: CampoDelCotejo;
  fechaNacimiento: CampoDelCotejo;
  global: 'coincide' | 'discrepante' | 'no_comprobable';
}

/** Una cara del documento: lo que el OCR y el MRZ leyeron de verdad. */
export interface LadoDeDocumento {
  mrz_valid?: boolean;
  mrz_fields?: Record<string, unknown> | null;
  ocr_fields?: Record<string, string> | null;
  confidence?: number;
  foto_extraida?: boolean;
}

export interface IdentidadDeExpediente {
  fuente?: 'registro' | 'declarado' | null;
  cotejo?: CotejoDeIdentidad | null;
  registroConsultado?: boolean;
  registroDisponible?: boolean;
  fuenteRegistro?: 'cache' | 'dondato' | null;
  personaEncontrada?: boolean;
  fallecida?: boolean;
  tripleMatch?: {
    passed: boolean;
    composite_score: number;
    match_selfie_doc: number;
    match_doc_gov: number;
    match_selfie_gov: number;
    error?: string | null;
  } | null;
}

export interface SenalesDeExpediente {
  vida?: { estado: string; motivos: string[] } | null;
  pulso?: {
    alive: boolean;
    bpm?: number | null;
    snr: number;
    quality: string;
    frames_used: number;
  } | null;
  ruido?: { consistent: boolean; intra_corr: number } | null;
  identidad?: IdentidadDeExpediente | null;
  documentoAnverso?: LadoDeDocumento | null;
  documentoReverso?: LadoDeDocumento | null;
  /** Lo demás (comparacion, fotogramas, fps…) se enseña en crudo como fallback. */
  [clave: string]: unknown;
}

/**
 * Una verificación, como la ve el cliente.
 *
 * `motivos` y `riesgo` son opcionales porque **dependen del perfil de exposición**, que fija
 * el servidor. `reasons` y `signals` solo llegan cuando el actor es ADMIN (el expediente).
 * El front no decide qué mostrar: dibuja lo que llegó. Si no llegó, no existe.
 */
export interface Verificacion {
  id: string;
  estado: Estado;
  persona: Persona | null;
  /** `null` = no se sabe cuánto costó. Se OMITE en pantalla; nunca se pinta como cero. */
  costo: string | null;
  creadaEn: string;
  motivos?: { codigo: string; texto: string }[];
  riesgo?: 'bajo' | 'medio' | 'alto';
  /** Los códigos crudos del veredicto. SOLO con rol ADMIN. */
  reasons?: string[];
  /** Las señales completas del expediente. SOLO con rol ADMIN. */
  signals?: SenalesDeExpediente | null;
}

export interface FilaHistorial {
  id: string;
  estado: Estado;
  externalRef: string | null;
  /** El nombre que devolvió la fuente. `null` si el documento no existía o ya se purgó. */
  nombre: string | null;
  /** `····3333`. Nunca el documento completo. */
  documento: string | null;
  creadaEn: string;
  resuelta: boolean;
}

export interface Resumen {
  verificaciones: number;
  enRevision: number;
  gastoDelMes: string;
  /** `null` cuando todavía no hay verificaciones. No es cero: es que no se puede calcular. */
  costoPorPersona: string | null;
  saldo: string;
  moneda: string;
  exposicion: Exposicion;
  modoRevision: ModoRevision;
}

export interface CeldaPrecio {
  price: string | null;
  margin: string | null;
  marginPct: number | null;
}

export interface ServicioAdmin {
  key: string;
  name: string;
  /** `String?` en el esquema. Nulable de verdad — no lo declares `string` "porque siempre viene". */
  description: string | null;
  /** `null` = no sabemos cuánto nos cuesta. El panel lo muestra vacío, JAMÁS como cero. */
  cost: string | null;
  costKnown: boolean;
  prices: Record<Tramo, CeldaPrecio>;
}

export interface GrillaAdmin {
  currency: string;
  tiers: Tramo[];
  services: ServicioAdmin[];
}

// ── Face ID: marca y sesiones ────────────────────────────────────────────────────

/**
 * La marca blanca del cliente.
 *
 * `null` en cada campo no es "vacío": es **"usa el defecto de Valídate"**. Esa distinción es
 * la que permite quitarse el logo; si `null` y "no mandado" fueran lo mismo, un cliente solo
 * podría cambiar su logo por otro, nunca quitarlo. La API lo respeta del mismo modo.
 */
export interface Marca {
  logoUrl: string | null;
  acento: string | null;
  acento2: string | null;
  tipografia: string | null;
}

/**
 * Lo que devuelve `GET`/`PUT /verificacion/marca`.
 *
 * Estos tipos NO están inventados: son copia de `MarcaConAvisos` en
 * `apps/api/src/modules/verificacion/marca.service.ts`, que es lo que el controlador declara
 * devolver. No es lo mismo que haber llamado al endpoint —la regla de arriba sigue en pie—
 * pero es una afirmación tomada del código que responde, no de mi memoria.
 *
 * `advertencias` NO son errores. Son cosas que **se guardaron y no se van a aplicar**. Un
 * `PUT` con un acento ilegible devuelve 200: el color del cliente es suyo y no lo rechazamos.
 */
export interface MarcaConAvisos {
  marca: Marca;
  advertencias: string[];
}

/** Lo que devuelve `POST /verificacion/sesion`. El token en claro va dentro de `url`, una vez. */
export interface SesionCreada {
  id: string;
  url: string;
  expiraEn: string;
}

// ── Llamadas ─────────────────────────────────────────────────────────────────────

export const api = {
  entrar: (email: string, password: string) =>
    pedir<{ token: string }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),

  yo: () => pedir<Yo>('/auth/me'),

  verificar: (documento: string, externalRef?: string) =>
    pedir<Verificacion>('/verifications', {
      method: 'POST',
      body: JSON.stringify({ documento, ...(externalRef ? { externalRef } : {}) }),
    }),

  historial: () => pedir<FilaHistorial[]>('/verifications'),
  verificacion: (id: string) => pedir<Verificacion>(`/verifications/${id}`),
  resumen: () => pedir<Resumen>('/verifications/resumen'),

  marca: () => pedir<MarcaConAvisos>('/verificacion/marca'),

  /**
   * Guarda la marca.
   *
   * Se manda `Marca` entera —con sus `null` explícitos— y no un objeto parcial: aquí el `null`
   * es el mensaje. Omitir una clave le dice al servidor "déjala como está"; mandarla en `null`
   * le dice "bórrala". Son cosas distintas y la pantalla necesita las dos.
   */
  guardarMarca: (m: Marca) =>
    pedir<MarcaConAvisos>('/verificacion/marca', { method: 'PUT', body: JSON.stringify(m) }),

  /**
   * Crea una sesión de verificación y devuelve el enlace.
   *
   * El panel puede llamarlo con la sesión normal: el `AuthGuard` acepta Bearer de sesión o API
   * key, y esta ruta no exige rol. Comprobado en `apps/api/src/common/guards.ts`, no supuesto —
   * si fuera solo para API keys, este botón devolvería 401 y la página nacería rota.
   *
   * `documento` es el número COMPLETO y viaja SOLO para que el servidor consulte el registro
   * y caliente la caché RENIEC (prefetch); el servidor NO lo persiste en claro. Si va
   * `documento` sin `documentoEnmascarado`, el servidor deriva la máscara solo — por eso las
   * claves se omiten cuando están vacías, en vez de mandarlas en blanco.
   */
  crearSesion: (datos?: { documentoEnmascarado?: string; documento?: string }) =>
    pedir<SesionCreada>('/verificacion/sesion', {
      method: 'POST',
      body: JSON.stringify({
        ...(datos?.documentoEnmascarado ? { documentoEnmascarado: datos.documentoEnmascarado } : {}),
        ...(datos?.documento ? { documento: datos.documento } : {}),
      }),
    }),

  grillaAdmin: (moneda = 'PEN') => pedir<GrillaAdmin>(`/admin/pricing?currency=${moneda}`),

  fijarPrecio: (serviceKey: string, tier: Tramo, currency: string, amount: string) =>
    pedir<{ ok: true }>('/admin/pricing', {
      method: 'PUT',
      body: JSON.stringify({ serviceKey, tier, currency, amount: Number(amount) }),
    }),

  /** `null` = "no sabemos cuánto cuesta". Es un valor válido, no un campo sin llenar. */
  fijarCosto: (serviceKey: string, cost: string | null) =>
    pedir<{ ok: true }>('/admin/cost', {
      method: 'PUT',
      body: JSON.stringify({ serviceKey, cost: cost === null ? null : Number(cost) }),
    }),
};
